import { cn } from '@forest/shared'
import { AnimatePresence } from 'motion/react'
import { useEffect, useState } from 'react'
import jumpChevronIcon from '~/assets/images/jump-chevron.svg'
import gemIcon from '~/features/reward-flow/assets/gem.svg'
import {
  getJumpPillGroupTop,
  JUMP_PILL_HEIGHT_PX,
} from '~/helpers/get-jump-pill-group-top'
import { getProgressBubbleTop } from '~/helpers/get-progress-bubble-top'
import type { CatchupPhase } from '~/hooks/use-catchup-animation'
import { motion } from '~/lib/motion'
import { m } from '~/paraglide/messages'
import { CatchupSeedGlow, EndingBurst, ProgressComet } from './catchup-glow'

export const CHECKPOINT_SIZE = 40
const GAP = 84
export const STEP = CHECKPOINT_SIZE + GAP

/** How long a locked pill's tooltip stays up after a tap. */
const LOCKED_TOOLTIP_DURATION_MS = 5000
/** Tooltip box height (h-6) + its gap above the pill group. */
const LOCKED_TOOLTIP_OFFSET_PX = 24 + 4

export interface JumpPointPill {
  requiredScore: number
  gemPrice: number
  status: 'purchasable' | 'locked'
  /** Whole days (ceil) until a locked point opens; 0 once purchasable. */
  daysUntilAvailable: number
}

export function ProgressColumn({
  current,
  checkpoints,
  jumpPoints,
  onJumpPointClick,
  onJumpPointPillRef,
  catchup,
}: {
  current: number
  checkpoints: number[]
  jumpPoints: JumpPointPill[]
  onJumpPointClick: (jumpPoint: JumpPointPill) => void
  /**
   * Hands each jump point pill's DOM node to the Jump anchor's viewport
   * tracking; called with null when a pill unmounts.
   */
  onJumpPointPillRef: (requiredScore: number, node: HTMLElement | null) => void
  /** The post-jump catch-up run's view state, from useCatchupAnimation. */
  catchup: {
    /**
     * Continuous top override (px) for the fill bar head / bubble while the
     * run glides between segment midpoints; null when idle.
     */
    displayTop: number | null
    /** Drives the catch-up glow decorations rendered on the track. */
    phase: CatchupPhase
    /** The track's coordinate space, for the catch-up scroll follow. */
    trackRef: React.RefObject<HTMLDivElement | null>
  }
}) {
  // Tapping a locked pill shows its "available in N days" tooltip for 5s.
  // A re-tap bumps the token so the timer restarts instead of toggling; a
  // tap on another locked pill replaces the tooltip (one at a time); taps
  // elsewhere don't dismiss it — it always just times out.
  const [lockedTooltip, setLockedTooltip] = useState<{
    requiredScore: number
    token: number
  } | null>(null)

  useEffect(() => {
    if (!lockedTooltip) return
    const timer = setTimeout(
      () => setLockedTooltip(null),
      LOCKED_TOOLTIP_DURATION_MS,
    )
    return () => clearTimeout(timer)
  }, [lockedTooltip])

  // Resolved against the live pill list: if the point unlocks or leaves
  // the track while the tooltip is up, the tooltip fades out with it.
  const lockedTooltipPill =
    jumpPoints.find(
      (jumpPoint) =>
        jumpPoint.status === 'locked' &&
        jumpPoint.requiredScore === lockedTooltip?.requiredScore,
    ) ?? null

  const currentPointTop = `${(
    catchup.displayTop ?? getProgressBubbleTop(current, checkpoints, STEP)
  ).toFixed(1)}px`
  const catchupCenterY =
    catchup.displayTop === null
      ? null
      : catchup.displayTop + CHECKPOINT_SIZE / 2

  return (
    <div className="flex w-[105px] shrink-0 justify-center pt-2">
      <div className="relative" ref={catchup.trackRef}>
        {/* Base */}
        <div className="opacity-30">
          {/* Bar */}
          <div className="absolute bottom-[52px] left-1/2 top-5 w-[13px] -translate-x-1/2 rounded-[30px] bg-[#333]"></div>
          {/* Checkpoints */}
          <div className="flex flex-col gap-[84px]">
            {checkpoints.map((cp) => (
              <Checkpoint key={cp} type="pending" number={cp} />
            ))}
          </div>
        </div>

        {/* Progress */}
        <div className="absolute bottom-[52px] left-1/2 top-0 -translate-x-1/2">
          {/* Bar */}
          <div
            className="absolute left-1/2 top-5 w-[13px] -translate-x-1/2 rounded-[30px] bg-white"
            style={{
              height: currentPointTop,
              boxShadow: '0 0 4px 0 rgba(255, 255, 255, 0.80)',
            }}
          />
          {/* Checkpoints */}
          <div className="flex flex-col gap-[84px]">
            {checkpoints
              .filter((cp) => cp <= current)
              .map((cp) => (
                <Checkpoint key={cp} type="done" number={cp} />
              ))}
          </div>
        </div>

        {/* Catch-up glow decorations, behind the bubble */}
        <AnimatePresence>
          {catchupCenterY !== null && catchup.phase === 'approach' && (
            <CatchupSeedGlow key="seed" centerY={catchupCenterY} />
          )}
          {catchupCenterY !== null && catchup.phase === 'sprint' && (
            <ProgressComet key="comet" centerY={catchupCenterY} />
          )}
          {catchupCenterY !== null && catchup.phase === 'settle' && (
            <EndingBurst key="burst" centerY={catchupCenterY} />
          )}
        </AnimatePresence>

        {/* Current progress point */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{ top: currentPointTop }}
        >
          <Checkpoint type="current" number={current} />
        </div>

        {/* Jump point buttons — pill + double-chevron group per Figma
            "speed up btn" (node 23082:49264, file Lqc5IT4YdGMUgh0JWgO8zK,
            Focus Pass Events). The group floats above its target checkpoint
            (geometry in get-jump-pill-group-top.ts) with the exported "rec"
            chevron pointing down at the circle, overlapping its top by
            ~9px; the whole group is one tap target. Pill: Figma draws the
            3px gradient stroke CENTER-aligned on a 28px-tall pill, so the
            CSS box is 31px tall with a 3px transparent border and the
            dual-background technique; the chevron's -2.5px margin tucks it
            under the stroke's outside half back to Figma's 1px overlap. The
            borderless "speed up2" pill in the same frame (node 22985:48723)
            is a stale older design — every jump pill uses the
            gradient-border style (decided 2026-07-23). */}
        {jumpPoints.map((jumpPoint) => (
          <button
            key={jumpPoint.requiredScore}
            ref={(node) => {
              // Only purchasable pills feed the Jump anchor's viewport
              // tracking — the anchor never routes to a locked pill.
              if (jumpPoint.status === 'purchasable') {
                onJumpPointPillRef(jumpPoint.requiredScore, node)
              }
            }}
            className={cn(
              'absolute left-1/2 flex w-max -translate-x-1/2 flex-col items-center',
              jumpPoint.status === 'locked' && 'opacity-30',
            )}
            style={{
              top: getJumpPillGroupTop(
                jumpPoint.requiredScore,
                checkpoints,
                STEP,
                CHECKPOINT_SIZE,
              ),
            }}
            onClick={() => {
              if (jumpPoint.status === 'locked') {
                setLockedTooltip((previous) => ({
                  requiredScore: jumpPoint.requiredScore,
                  token: (previous?.token ?? 0) + 1,
                }))
                return
              }
              onJumpPointClick(jumpPoint)
            }}
          >
            <span
              className="flex items-center gap-0.5 rounded-[20px] border-[3px] border-transparent pl-0.5 pr-2"
              style={{
                height: JUMP_PILL_HEIGHT_PX,
                background:
                  'linear-gradient(#fff, #fff) padding-box, linear-gradient(137deg, #FFEAAF 15.4%, #D6FFF5 59.6%, #7EF7DB 100%) border-box',
                boxShadow: '0 -2px 9.3px 3px rgba(151, 233, 210, 0.70)',
              }}
            >
              <img className="size-5" src={gemIcon} alt="" />
              <span className="text-xs font-extrabold text-[#458B99]">
                {jumpPoint.gemPrice}
              </span>
            </span>
            <img
              className="-mt-[2.5px] h-[21.8px] w-[13px]"
              src={jumpChevronIcon}
              alt=""
            />
          </button>
        ))}

        {/* Locked-pill tooltip, floating above the pill group. Centering
            rides on Motion's x transform (not a Tailwind translate class)
            so the fade can never clobber it. */}
        <AnimatePresence>
          {lockedTooltipPill && (
            <motion.div
              key={lockedTooltipPill.requiredScore}
              className="pointer-events-none absolute left-1/2 flex h-6 w-max items-center rounded-full bg-[#333333] bg-opacity-75 px-2 text-xs text-[#c9c9c9]"
              style={{
                x: '-50%',
                top:
                  getJumpPillGroupTop(
                    lockedTooltipPill.requiredScore,
                    checkpoints,
                    STEP,
                    CHECKPOINT_SIZE,
                  ) - LOCKED_TOOLTIP_OFFSET_PX,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {m.available_in_x_days({
                number: lockedTooltipPill.daysUntilAvailable,
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function Checkpoint(props: {
  type: 'pending' | 'done' | 'current'
  number: number
}) {
  return (
    <div className="relative size-10 p-[2px]">
      {props.type === 'current' && (
        <div className="absolute inset-2 -z-10 animate-ping rounded-[50%] bg-white/80" />
      )}
      <div
        className={cn(
          'grid size-full place-items-center rounded-[50%]',
          props.type === 'pending' ? 'bg-[#333]' : 'bg-white',
        )}
      >
        {props.type === 'done' ? (
          props.number === 0 ? (
            <SaplingIcon />
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.99316 12L9.99321 16.0001L18.0074 8"
                stroke="#333333"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )
        ) : (
          <span
            className={cn(
              'text-center text-[10px] font-bold',
              props.type === 'current' ? 'text-[#333]' : 'text-white',
            )}
          >
            {props.type === 'current'
              ? m.countdown_minute({ minute: props.number })
              : props.number}
          </span>
        )}
      </div>
    </div>
  )
}

function SaplingIcon() {
  return (
    <svg
      width="23"
      height="17"
      viewBox="0 0 23 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.0255 0.0121089C19.3381 -0.0540649 20.6513 0.151613 21.881 0.615624C22.3333 0.786539 22.5994 1.25722 22.5118 1.73281C22.2766 3.00733 21.7861 4.22196 21.0714 5.30312C20.3567 6.38409 19.431 7.31011 18.3507 8.02578C18.3239 8.04353 18.2961 8.0605 18.2677 8.07559C17.1456 8.67069 15.9092 9.0208 14.6417 9.10293C13.8427 9.15465 13.0422 9.09844 12.2618 8.93887V15.2377C12.2618 15.7899 11.8141 16.2377 11.2618 16.2377C10.7096 16.2376 10.2618 15.7899 10.2618 15.2377V8.93984C9.48285 9.09875 8.68432 9.15449 7.88681 9.10293C6.61932 9.02086 5.38296 8.67066 4.26084 8.07559C4.23229 8.06044 4.2038 8.04362 4.17685 8.02578C3.09646 7.31007 2.17087 6.38417 1.45615 5.30312C0.741339 4.2219 0.251971 3.00743 0.0166949 1.73281C-0.0710487 1.25714 0.194113 0.786552 0.646578 0.615624C1.87628 0.151629 3.18938 -0.0540792 4.50205 0.0121089C5.65076 0.0701276 6.77883 0.335765 7.83115 0.793359L8.27744 1.00039L8.38877 1.06582C9.46837 1.78982 10.3752 2.74317 11.044 3.85781C11.121 3.98609 11.1936 4.11674 11.2638 4.24844C11.334 4.11668 11.4075 3.98615 11.4845 3.85781C12.1533 2.74322 13.0601 1.78977 14.1397 1.06582L14.2511 1.00039C15.4277 0.41507 16.713 0.0783939 18.0255 0.0121089ZM4.40146 2.01016C3.65799 1.97265 2.91466 2.0469 2.19541 2.22598C2.40954 2.92339 2.7211 3.5885 3.12509 4.19961C3.68204 5.04201 4.40061 5.76524 5.23837 6.32852C6.09984 6.77827 7.04561 7.045 8.01572 7.10781C8.73019 7.15402 9.44579 7.08724 10.1378 6.91641C9.97868 6.20276 9.70715 5.51709 9.3292 4.88711C8.82271 4.04296 8.13929 3.31869 7.32724 2.76309C6.41286 2.31801 5.41756 2.06149 4.40146 2.01016ZM18.127 2.01016C17.111 2.06142 16.1156 2.31811 15.2013 2.76309C14.3891 3.31867 13.7059 4.04293 13.1993 4.88711C12.8213 5.51721 12.5479 6.20261 12.3888 6.91641C13.0813 7.08752 13.7977 7.15411 14.5128 7.10781C15.4814 7.04501 16.4258 6.77906 17.2862 6.33047C18.1254 5.76686 18.8458 5.04311 19.4034 4.19961C19.8073 3.58865 20.118 2.92319 20.3321 2.22598C19.6132 2.047 18.8702 1.97273 18.127 2.01016Z"
        fill="#333333"
      />
    </svg>
  )
}
